import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, X, Send, ExternalLink, Bot } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FloatingChatWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  if (location.pathname === '/ai-chat') return null;

  const sendMessage = async (text = message) => {
    if (!text.trim() || loading) return;

    const userMsg = { id: Date.now().toString(), role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setMessage('');
    setLoading(true);

    const placeholderId = `ph-${Date.now()}`;
    setMessages(prev => [...prev, { id: placeholderId, role: 'assistant', content: '', streaming: true }]);

    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let ev;
          try { ev = JSON.parse(line.slice(6)); } catch { continue; }

          if (ev.type === 'start') {
            setSessionId(ev.session_id);
          } else if (ev.type === 'chunk') {
            accumulatedText += ev.content;
            setMessages(prev => prev.map(m =>
              m.id === placeholderId ? { ...m, content: accumulatedText } : m
            ));
          } else if (ev.type === 'done') {
            setMessages(prev => prev.map(m =>
              m.id === placeholderId ? { ...m, streaming: false } : m
            ));
          } else if (ev.type === 'error') {
            setMessages(prev => prev.map(m =>
              m.id === placeholderId
                ? { ...m, content: 'Something went wrong. Please try again.', streaming: false }
                : m
            ));
          }
        }
      }
    } catch {
      setMessages(prev => prev.filter(m => m.id !== placeholderId && m.id !== userMsg.id));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Expanded chat panel */}
      {open && (
        <div className="bg-white border border-[#e5e5e5] shadow-2xl flex flex-col" style={{ width: '520px', height: '640px' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#1a1a1a] shrink-0">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-[#ffd879]" />
              <span className="text-sm font-medium text-white">OlafAI</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/ai-chat')}
                className="text-[#4a4a4a] hover:text-white transition-colors"
                title="Open full chat"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setOpen(false)} className="text-[#4a4a4a] hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <Bot className="w-8 h-8 text-[#ffd879] mb-2" />
                <p className="text-sm font-medium text-[#1a1a1a]">Ask me anything</p>
                <p className="text-xs text-[#4a4a4a] mt-1">Portfolio data, deals, analytics — I'm here to help.</p>
              </div>
            )}
            {messages.map(msg => (
              <div key={msg.id} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-5 h-5 rounded-full bg-[#ffd879]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="w-3 h-3 text-[#1a1a1a]" />
                  </div>
                )}
                <div className={`max-w-[85%] px-3 py-2 text-xs leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#1a1a1a] text-white'
                    : 'bg-[#f9f9f9] text-[#1a1a1a]'
                }`}>
                  {msg.role === 'assistant'
                    ? <ReactMarkdown>{msg.content || (msg.streaming ? '▋' : '')}</ReactMarkdown>
                    : msg.content}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[#e5e5e5] shrink-0">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Ask a question..."
                className="flex-1 text-xs px-3 py-2 border border-[#e5e5e5] outline-none focus:border-[#ffd879] text-[#1a1a1a] bg-white"
                disabled={loading}
              />
              <button
                onClick={() => sendMessage()}
                disabled={loading || !message.trim()}
                className="px-3 py-2 bg-[#ffd879] text-[#1a1a1a] disabled:opacity-40 transition-opacity"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-16 h-16 bg-[#ffd879] hover:bg-[#f5cc6a] text-[#1a1a1a] shadow-xl flex items-center justify-center transition-all hover:scale-105"
        title="OlafAI"
      >
        {open ? <X className="w-7 h-7" /> : <MessageSquare className="w-7 h-7" />}
      </button>
    </div>
  );
}
